// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\planning\CyclesPage.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Table, Button, Space, Popconfirm, Typography, Select, Row, Col, DatePicker,
    Spin, Alert, Descriptions, message, List,
    Upload, Modal, Badge, Tag, Collapse, Avatar, Tooltip
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, FilterOutlined, CalendarOutlined, UserOutlined,
    UploadOutlined, DownloadOutlined, UsergroupAddOutlined, ClockCircleOutlined, AuditOutlined, 
    StarFilled, WarningOutlined, ReloadOutlined, CrownOutlined, RocketOutlined, 
    EnvironmentOutlined, CheckCircleOutlined, StopOutlined, AppstoreOutlined
} from '@ant-design/icons';
import Papa from 'papaparse';
import {
    fetchCycles, deleteCycle,
    importCyclesBatch
} from '../../redux/slices/planning/planningThunks';
import { getDelegatesList } from '../../redux/slices/delegates/delegatesThunk';
import { getDelegatesForPlanning } from '../../services/delegateService';
import { getAllDoctorConflicts } from '../../services/planning.service';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import CycleFormModal from './CycleFormModal';
import axiosInstance from '../../services/axiosServicesVisit';
import DoctorHistoryModal from '../../components/DoctorHistoryModal';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { Title, Text, Link } = Typography;
const { RangePicker } = DatePicker;
const dateFormat = 'YYYY-MM-DD';

// --- CONFIGURATION DE LA SEGMENTATION RELATIONNELLE AVEC COULEURS DE LA MODALE ---
export const RELATIONSHIP_CONFIG = {
  PARTNER_ACTIVE: {
    label: "⭐ Partenaire Stratégique",
    tagColor: "gold",
    icon: <StarFilled />,
    bgColor: "#fffbe6",
    borderColor: "#fadb14",
    titleColor: "#856404",
    shadow: "0 4px 12px rgba(250, 219, 20, 0.18)"
  },
  PARTNER_AT_RISK: {
    label: "⚠️ Partenaire Sous Vigilance",
    tagColor: "warning",
    icon: <WarningOutlined />,
    bgColor: "#fff7e6",
    borderColor: "#fa8c16",
    titleColor: "#d46b08",
    shadow: "0 4px 12px rgba(250, 140, 22, 0.18)"
  },
  KEY_PRESCRIBER: {
    label: "🚀 Prescripteur Clé (A+)",
    tagColor: "success",
    icon: <RocketOutlined />,
    bgColor: "#f6ffed",
    borderColor: "#52c41a",
    titleColor: "#237804",
    shadow: "0 4px 12px rgba(82, 196, 26, 0.18)"
  },
  STANDARD: {
    label: "👤 Standard",
    tagColor: "default",
    icon: <UserOutlined />,
    bgColor: "#f0f5ff",
    borderColor: "#adc6ff",
    titleColor: "#002766",
    shadow: "none"
  },
  REFRACTORY: {
    label: "⛔ Non Réceptif",
    tagColor: "error",
    icon: <StopOutlined />,
    bgColor: "#fff1f0",
    borderColor: "#ff4d4f",
    titleColor: "#cf1322",
    shadow: "0 4px 12px rgba(255, 77, 79, 0.18)"
  }
};

const extractCategory = (p) => {
    if (!p) return 'STANDARD';
    const val = 
        p.relationshipCategory || 
        p.prescriptionProfile || 
        p.prescription || 
        p.profile || 
        p.segment || 
        p.doctor?.prescriptionProfile ||
        p.doctor?.prescription ||
        '';

    const str = String(val).toUpperCase().trim();
    if (str === 'REFRACTORY' || str.includes('RÉCEPTIF') || str.includes('RECEPTIF') || str.includes('REFRACTAIRE')) {
        return 'REFRACTORY';
    }
    if (str === 'PARTNER_AT_RISK' || str.includes('VIGILANCE')) {
        return 'PARTNER_AT_RISK';
    }
    if (str === 'KEY_PRESCRIBER' || str.includes('A+')) {
        return 'KEY_PRESCRIBER';
    }
    if (str === 'PARTNER_ACTIVE' || p.isPartner === true || p.isPartner === 'true' || p.isPartner === 1) {
        return 'PARTNER_ACTIVE';
    }
    return str || 'STANDARD';
};

const formatDate = (dateString) => { 
  if (!dateString) return 'N/D'; 
  const d = dayjs(dateString); 
  return d.isValid() ? d.format('DD/MM/YYYY') : 'Inv.'; 
};

const getCyclePeriod = (weeks) => { 
  if (!weeks || !Array.isArray(weeks) || weeks.length === 0) { 
    return { overallStartDate: null, overallEndDate: null }; 
  } 
  const sortedWeeks = [...weeks].sort((a, b) => (a.week_number ?? Infinity) - (b.week_number ?? Infinity)); 
  const firstWeekStartDate = sortedWeeks[0]?.start_date; 
  const lastWeekEndDate = sortedWeeks[sortedWeeks.length - 1]?.end_date; 
  return { overallStartDate: firstWeekStartDate, overallEndDate: lastWeekEndDate }; 
};

const getCategoryTagColor = (cat) => {
    if (!cat) return 'default';
    const c = String(cat).toUpperCase();
    if (c === 'AKTIV') return 'blue';
    if (c === 'SANOSAN') return 'green';
    if (c === 'MATERNITE') return 'purple';
    return 'cyan';
};

function CyclesPage() {
  const dispatch = useDispatch();
  const allCycles = useSelector((state) => state.planning?.cycles || []);
  const loadingCycles = useSelector((state) => state.planning?.loading?.cycles || false);
  const planningError = useSelector((state) => state.planning?.error);
  const reduxDelegates = useSelector((state) => state.delegates?.delegatesList?.data?.data || []);

  const [delegatesList, setDelegatesList] = useState([]);
  const [loadingDelegates, setLoadingDelegates] = useState(true);
  const [errorDelegates, setErrorDelegates] = useState(null);
  
  // --- FILTRES GLOBAUX ---
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [selectedDelegateFilter, setSelectedDelegateFilter] = useState(undefined);
  const [selectedDateRangeFilter, setSelectedDateRangeFilter] = useState([null, null]);
  const [selectedDaysFilter, setSelectedDaysFilter] = useState('all');

  const [isAddEditModalVisible, setIsAddEditModalVisible] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);

  const [uploadingCycles, setUploadingCycles] = useState(false);
  const [cycleFileList, setCycleFileList] = useState([]);
  const [parsedCycleData, setParsedCycleData] = useState(null);
  const [parseCycleError, setParseCycleError] = useState(null);

  const [isPersonUploadModalVisible, setIsPersonUploadModalVisible] = useState(false);
  const [currentCycleForPersonManagement, setCurrentCycleForPersonManagement] = useState(null);
  const [uploadingPersons, setUploadingPersons] = useState(false);
  const [personUploadResult, setPersonUploadResult] = useState(null);
  const [personUploadFileList, setPersonUploadFileList] = useState([]);
  const [fileToUploadForPersons, setFileToUploadForPersons] = useState(null); 

  const [isWeeklyPlanUploadModalVisible, setIsWeeklyPlanUploadModalVisible] = useState(false);
  const [weeklyPlanFile, setWeeklyPlanFile] = useState(null);
  const [uploadingWeeklyPlan, setUploadingWeeklyPlan] = useState(false);

  // --- VISITES RAPPROCHÉES (< 5j) ---
  const [frequentVisitsAlerts, setFrequentVisitsAlerts] = useState([]);
  const [loadingFrequentVisits, setLoadingFrequentVisits] = useState(false);

  // --- CONFLITS D'ASSIGNATION DE MÉDECINS ---
  const [assignmentConflicts, setAssignmentConflicts] = useState([]);
  const [loadingConflicts, setLoadingConflicts] = useState(false);

  // 1. REGROUPEMENT UNIQUE DE TOUS LES DÉLÉGUÉS AVEC GAMME
  const allKnownDelegates = useMemo(() => {
    const map = new Map();
    [...(reduxDelegates || []), ...(delegatesList || [])].forEach(d => {
      if (d && d.id) {
        const prev = map.get(d.id);
        map.set(d.id, { ...prev, ...d, category: d.category || prev?.category });
      }
    });
    return Array.from(map.values());
  }, [reduxDelegates, delegatesList]);

  // 2. DÉLÉGUÉS DISPONIBLES POUR LE SELECT SELON LA GAMME SÉLECTIONNÉE
  const availableDelegatesForSelect = useMemo(() => {
    let list = delegatesList;
    if (selectedCategoryFilter && selectedCategoryFilter !== 'all') {
      list = list.filter(d => {
        const cat = d.category || allKnownDelegates.find(kd => kd.id === d.id)?.category;
        return cat === selectedCategoryFilter;
      });
    }
    return list;
  }, [delegatesList, selectedCategoryFilter, allKnownDelegates]);

  // 3. REGROUPEMENT PAR MÉDECIN AVEC TRI CROISSANT DES CYCLES (Cycle 1, Cycle 2...)
  const groupedAssignmentConflicts = useMemo(() => {
    if (!assignmentConflicts || assignmentConflicts.length === 0) return [];

    let filtered = assignmentConflicts;

    if (selectedCategoryFilter && selectedCategoryFilter !== 'all') {
      const targetCat = String(selectedCategoryFilter).trim().toUpperCase();
      filtered = filtered.filter(item => {
        const cat1 = String(allKnownDelegates.find(d => Number(d.id) === Number(item.delegate1Id))?.category || '').trim().toUpperCase();
        const cat2 = String(allKnownDelegates.find(d => Number(d.id) === Number(item.delegate2Id))?.category || '').trim().toUpperCase();
        return cat1 === targetCat && cat2 === targetCat;
      });
    }

    if (selectedDelegateFilter) {
      const targetId = Number(selectedDelegateFilter);
      const targetDelegate = delegatesList.find(d => Number(d.id) === targetId);
      const targetName = targetDelegate 
        ? `${targetDelegate.firstname} ${targetDelegate.lastname}`.toLowerCase().trim() 
        : '';

      filtered = filtered.filter(item => {
        // ⚡ Match par ID numérique
        const matchId = Number(item.delegate1Id) === targetId || Number(item.delegate2Id) === targetId;
        if (matchId) return true;

        // ⚡ Match par Nom complet (au cas où les IDs de l'API diffèrent)
        if (targetName) {
          const name1 = String(item.delegate1Name || '').toLowerCase();
          const name2 = String(item.delegate2Name || '').toLowerCase();
          return name1.includes(targetName) || name2.includes(targetName);
        }

        return false;
      });
    }

    const map = new Map();

    filtered.forEach(item => {
      const key = `${item.personId}_${item.delegate1Id}_${item.delegate2Id}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          personId: item.personId,
          doctorName: item.doctorName,
          specialtyName: item.specialtyName,
          regionName: item.regionName,
          isPartner: item.isPartner,
          relationshipCategory: item.relationshipCategory || (item.isPartner ? 'PARTNER_ACTIVE' : 'STANDARD'),
          delegate1Id: item.delegate1Id,
          delegate1Name: item.delegate1Name,
          delegate2Id: item.delegate2Id,
          delegate2Name: item.delegate2Name,
          conflictsList: []
        });
      }

      const group = map.get(key);
      group.conflictsList.push({
        cycle1Name: item.cycle1Name,
        week1Number: item.week1Number,
        week1Start: item.week1Start,
        week1End: item.week1End,
        cycle2Name: item.cycle2Name,
        week2Number: item.week2Number,
        week2Start: item.week2Start,
        week2End: item.week2End,
      });
    });

    const extractCycleNumber = (name) => {
      const match = String(name || '').match(/\d+/);
      return match ? parseInt(match[0], 10) : 0;
    };

    map.forEach(group => {
      group.conflictsList.sort((a, b) => {
        const numA = extractCycleNumber(a.cycle1Name);
        const numB = extractCycleNumber(b.cycle1Name);
        if (numA !== numB) return numA - numB;
        return dayjs(a.week1Start).valueOf() - dayjs(b.week1Start).valueOf();
      });
    });

    return Array.from(map.values());
  }, [assignmentConflicts, selectedDelegateFilter, selectedCategoryFilter, allKnownDelegates]);

  const filteredFrequentVisits = useMemo(() => {
    if (!frequentVisitsAlerts || frequentVisitsAlerts.length === 0) return [];

    let list = frequentVisitsAlerts;

    if (selectedCategoryFilter && selectedCategoryFilter !== 'all') {
      const targetCat = String(selectedCategoryFilter).trim().toUpperCase();
      list = list.filter(item => {
        const delCats = (item.visits || []).map(v => {
          const d = allKnownDelegates.find(kd => 
            Number(kd.id) === Number(v.delegateId) ||
            `${kd.firstname} ${kd.lastname}`.trim().toLowerCase() === String(v.delegateName).trim().toLowerCase()
          );
          return String(d?.category || '').trim().toUpperCase();
        });
        return delCats.length > 0 && delCats.every(c => c === targetCat);
      });
    }

    if (selectedDelegateFilter) {
      const targetId = Number(selectedDelegateFilter);
      const targetDelegate = delegatesList.find(d => Number(d.id) === targetId);
      const targetName = targetDelegate ? `${targetDelegate.firstname} ${targetDelegate.lastname}`.toLowerCase().trim() : '';

      list = list.filter(item => {
        if (item.visits && item.visits.some(v => Number(v.delegateId) === targetId)) return true;
        if (item.delegatesIdsList && item.delegatesIdsList.map(Number).includes(targetId)) return true;
        if (Number(item.delegate1Id) === targetId || Number(item.delegate2Id) === targetId) return true;

        if (targetName) {
          if (item.delegatesNamesList && item.delegatesNamesList.some(n => String(n).toLowerCase().includes(targetName))) return true;
          if (item.visits && item.visits.some(v => String(v.delegateName).toLowerCase().includes(targetName))) return true;
        }

        return false;
      });
    }

    if (selectedDaysFilter && selectedDaysFilter !== 'all') {
      list = list.filter(item => {
        const match = String(item.totalSpanDays || item.daysDiff || '').match(/\d+/);
        const days = match ? parseInt(match[0], 10) : 1;

        if (selectedDaysFilter === 'lte_2') return days <= 2;
        if (selectedDaysFilter === 'lte_3') return days <= 3;
        return days === parseInt(selectedDaysFilter, 10);
      });
    }

    return list;
  }, [frequentVisitsAlerts, selectedCategoryFilter, selectedDelegateFilter, selectedDaysFilter, delegatesList, allKnownDelegates]);

  // --- PARCOURS DU MÉDECIN (MODAL) ---
  const [doctorModalVisible, setDoctorModalVisible] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);

  const handleOpenDoctorHistory = (personId) => {
    setSelectedDoctorId(personId);
    setDoctorModalVisible(true);
  };

  const isMounted = useRef(true);

  useEffect(() => { 
    isMounted.current = true; 
    return () => { isMounted.current = false; }; 
  }, []);

  useEffect(() => {
    setLoadingDelegates(true); 
    setErrorDelegates(null);
    getDelegatesForPlanning()
      .then(r => { 
          if (!isMounted.current) return;
          const data = Array.isArray(r?.data) ? r.data : [];
          setDelegatesList(data); 
      })
      .catch(e => { 
        if (!isMounted.current) return; 
        setErrorDelegates("Err délég."); 
        setDelegatesList([]); 
      })
      .finally(() => { 
        if (isMounted.current) setLoadingDelegates(false); 
      });
  }, []);

  useEffect(() => { 
    dispatch(fetchCycles()); 
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
  }, [dispatch]);

  const handleDelete = (id) => { 
    dispatch(deleteCycle(id)).unwrap()
      .then(() => message.success('Cycle supprimé.'))
      .catch(() => message.error('Erreur suppression.')); 
  };

  const showAddModal = () => { 
    setEditingCycle(null); 
    setIsAddEditModalVisible(true); 
  };

  const showEditModal = (cycle) => { 
    if (cycle && cycle.id != null) { 
      setEditingCycle(cycle); 
      setIsAddEditModalVisible(true); 
    } else { 
      message.error("Données cycle invalides."); 
    } 
  };

  const handleAddEditCancel = () => { 
    setIsAddEditModalVisible(false); 
    setEditingCycle(null); 
  };

  const handleDelegateFilterChange = (value) => { 
    setSelectedDelegateFilter(value); 
  };

  const handleDateRangeFilterChange = (dates) => { 
    setSelectedDateRangeFilter(dates || [null, null]); 
  };

  // UPLOAD CSV CYCLES BATCH
  const handleBeforeCycleUpload = (file) => { 
    const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'); 
    if (!isCsv) { 
      message.error('Fichier CSV uniquement!'); 
      return Upload.LIST_IGNORE; 
    } 
    setCycleFileList([file]); 
    setParsedCycleData(null); 
    setParseCycleError(null); 
    setUploadingCycles(true); 
    
    Papa.parse(file, { 
      header: true, 
      skipEmptyLines: true, 
      complete: (results) => { 
        if (!isMounted.current) return; 
        if (results.errors.length > 0) { 
          const errorMsg = `Erreur parsing Ligne ${results.errors[0].row + 1}: ${results.errors[0].message}`; 
          setParseCycleError(errorMsg); 
          message.error("Erreur lecture CSV (voir détails)."); 
          setParsedCycleData(null); 
          setCycleFileList([]); 
        } else if (!results.data || results.data.length === 0) { 
          setParseCycleError("Fichier CSV vide."); 
          message.warn("Fichier CSV vide."); 
          setParsedCycleData([]); 
        } else { 
          const requiredHeaders = ['delegateEmail', 'objectifSemaine', 'debutS1', 'finS1']; 
          const actualHeaders = results.meta.fields; 
          const missingHeaders = requiredHeaders.filter(h => !actualHeaders?.includes(h)); 
          if (missingHeaders.length > 0) { 
            const errorMsg = `Colonnes manquantes pour import de cycle: ${missingHeaders.join(', ')}`; 
            setParseCycleError(errorMsg); 
            message.error(errorMsg); 
            setParsedCycleData(null); 
            setCycleFileList([]); 
          } else { 
            setParsedCycleData(results.data); 
            setParseCycleError(null); 
            message.success(`${results.data.length} cycles prêts pour import.`); 
          } 
        } 
        setUploadingCycles(false); 
      }, 
      error: (error) => { 
        if (!isMounted.current) return; 
        setParseCycleError("Erreur lecture fichier."); 
        message.error("Erreur lecture fichier CSV."); 
        setParsedCycleData(null); 
        setCycleFileList([]); 
        setUploadingCycles(false); 
      } 
    }); 
    return false; 
  };

  const uploadCycleProps = { 
    onRemove: () => { 
      setCycleFileList([]); 
      setParsedCycleData(null); 
      setParseCycleError(null); 
      return true; 
    }, 
    beforeUpload: handleBeforeCycleUpload, 
    fileList: cycleFileList, 
    maxCount: 1, 
    accept: ".csv", 
  };

  const handleImportParsedCycleData = () => { 
    if (!parsedCycleData || parsedCycleData.length === 0 || parseCycleError || uploadingCycles) { 
      if (parseCycleError) message.error(`Corrigez l'erreur fichier: ${parseCycleError}`); 
      else message.error("Aucune donnée de cycle valide à importer."); 
      return; 
    } 
    setUploadingCycles(true); 
    dispatch(importCyclesBatch(parsedCycleData)).unwrap()
      .then((result) => { 
        if (!isMounted.current) return; 
        const createdCount = result?.created ?? 0; 
        const errorCount = result?.errors ?? 0; 
        const details = result?.errorDetails || []; 
        if (errorCount > 0) { 
          message.error(`Import de cycles terminé avec ${errorCount} erreur(s). Voir détails.`); 
          Modal.error({ 
            title: `Échec import pour ${errorCount} cycle(s)`, 
            content: ( 
              <div style={{ marginTop: 16 }}> 
                <p>Erreurs rencontrées :</p> 
                <List size="small" bordered dataSource={details} renderItem={(item) => <List.Item><Text type="danger">{item}</Text></List.Item>} style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }} /> 
              </div> 
            ), 
            width: 600, 
            maskClosable: true 
          }); 
        } else { 
          message.success(`Importation de cycles réussie ! ${createdCount} cycle(s) créé(s).`); 
        } 
        dispatch(fetchCycles()); 
      })
      .catch(() => {})
      .finally(() => { 
        if (isMounted.current) { 
          setUploadingCycles(false); 
          setCycleFileList([]); 
          setParsedCycleData(null); 
          setParseCycleError(null); 
        } 
      }); 
  };

  // UPLOAD CSV PERSONNES
  const handleOpenPersonUploadModal = (cycle) => {
    setCurrentCycleForPersonManagement(cycle);
    setPersonUploadResult(null);
    setPersonUploadFileList([]);
    setFileToUploadForPersons(null);
    setIsPersonUploadModalVisible(true);
  };

  const handlePersonUploadModalCancel = () => {
    setIsPersonUploadModalVisible(false);
    setCurrentCycleForPersonManagement(null);
    setPersonUploadResult(null);
    setPersonUploadFileList([]);
    setFileToUploadForPersons(null);
  };

  const handleBeforePersonUpload = (file) => {
    const isCSV = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
    if (!isCSV) {
        message.error(`${file.name} n'est pas un fichier CSV.`);
        setFileToUploadForPersons(null);
        setPersonUploadFileList([]);
        return Upload.LIST_IGNORE;
    }
    setFileToUploadForPersons(file);
    setPersonUploadFileList([file]);
    setPersonUploadResult(null);
    return false;
  };

  const triggerManualPersonUpload = async () => {
    if (!fileToUploadForPersons) {
        message.error("Veuillez d'abord sélectionner un fichier CSV.");
        return;
    }
    if (!currentCycleForPersonManagement?.id) {
        message.error("Erreur: Cycle non identifié pour l'assignation des personnes.");
        return;
    }

    const formData = new FormData();
    formData.append('file', fileToUploadForPersons);

    setUploadingPersons(true);
    setPersonUploadResult(null);
    const uploadUrl = `/v1/cycles/${currentCycleForPersonManagement.id}/persons/upload-csv`;

    try {
        const response = await axiosInstance.post(uploadUrl, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success(response.data.message || 'Fichier de personnes traité avec succès.');
        setPersonUploadResult(response.data);
        dispatch(fetchCycles());
        setFileToUploadForPersons(null); 
        setPersonUploadFileList([]); 
    } catch (error) {
        const apiErrorMsg = error.response?.data?.message || 'Erreur lors de l\'upload du fichier des personnes.';
        message.error(apiErrorMsg);
        setPersonUploadResult({ message: apiErrorMsg, errors: [error.response?.data?.detail || error.response?.data?.message || String(error)] });
    } finally {
        if (isMounted.current) {
            setUploadingPersons(false);
        }
    }
  };

  const personUploadProps = {
    name: 'file',
    multiple: false,
    fileList: personUploadFileList,
    onRemove: () => {
        setPersonUploadFileList([]);
        setFileToUploadForPersons(null);
        setPersonUploadResult(null);
        return true;
    },
    beforeUpload: handleBeforePersonUpload,
    accept: ".csv",
    disabled: uploadingPersons,
  };

  // PLAN HEBDOMADAIRE (CSV)
  const handleDownloadWeeklyPlan = async (cycleId, cycleName) => {
    message.loading({ content: 'Préparation du téléchargement...', key: 'downloadPlan', duration: 0 });
    try {
        const response = await axiosInstance.get(`/v1/cycles/${cycleId}/weekly-plan/download-csv`, {
            responseType: 'blob',
        });
        const safeName = cycleName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || `cycle_${cycleId}`;
        const filename = `plan_hebdomadaire_${safeName}.csv`;

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success({ content: 'Téléchargement démarré.', key: 'downloadPlan', duration: 2 });
    } catch (error) {
        message.error({ content: 'Erreur lors du téléchargement.', key: 'downloadPlan', duration: 3 });
    }
  };

  const handleOpenWeeklyPlanUploadModal = (cycle) => {
    setCurrentCycleForPersonManagement(cycle);
    setIsWeeklyPlanUploadModalVisible(true);
    setWeeklyPlanFile(null);
  };

  const handleWeeklyPlanUploadModalCancel = () => {
    setIsWeeklyPlanUploadModalVisible(false);
    setCurrentCycleForPersonManagement(null);
  };

  const handleBeforeWeeklyPlanUpload = (file) => {
    setWeeklyPlanFile(file);
    return false;
  };

  const triggerManualWeeklyPlanUpload = async () => {
    if (!weeklyPlanFile || !currentCycleForPersonManagement?.id) return;
    
    const formData = new FormData();
    formData.append('file', weeklyPlanFile);
    setUploadingWeeklyPlan(true);
    
    try {
        const url = `/v1/cycles/${currentCycleForPersonManagement.id}/weekly-plan/upload-csv`;
        await axiosInstance.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        message.success('Plan hebdomadaire mis à jour avec succès !');
        dispatch(fetchCycles());
        setIsWeeklyPlanUploadModalVisible(false);
    } catch (error) {
        const apiErrorMsg = error.response?.data?.message || 'Erreur lors de la mise à jour.';
        message.error(apiErrorMsg);
    } finally {
        if (isMounted.current) {
            setUploadingWeeklyPlan(false);
        }
    }
  };

  // ALERTES 1 : VISITES RAPPROCHÉES (< 5j)
  const fetchFrequentVisits = useCallback(async () => {
    setLoadingFrequentVisits(true);
    try {
      const params = {};
      if (selectedCategoryFilter && selectedCategoryFilter !== 'all') {
        params.category = selectedCategoryFilter;
      }
      if (selectedDelegateFilter) params.delegateId = selectedDelegateFilter;
      if (selectedDateRangeFilter && selectedDateRangeFilter[0] && selectedDateRangeFilter[1]) {
        params.startDate = selectedDateRangeFilter[0].format('YYYY-MM-DD');
        params.endDate = selectedDateRangeFilter[1].format('YYYY-MM-DD');
      }
      const res = await axiosInstance.get('/v1/dashboard/alerts/frequent-visits', { params });
      setFrequentVisitsAlerts(res.data || []);
    } catch (e) {
      console.error("Erreur chargement visites rapprochées :", e);
    } finally {
      setLoadingFrequentVisits(false);
    }
  }, [selectedCategoryFilter, selectedDelegateFilter, selectedDateRangeFilter]);

  useEffect(() => {
    fetchFrequentVisits();
  }, [fetchFrequentVisits]);

  const handleExportFrequentVisits = () => {
    const listToExport = filteredFrequentVisits;
    if (!listToExport || listToExport.length === 0) {
      message.warning("Aucune visite rapprochée à exporter pour cette sélection.");
      return;
    }

    const headers = [
      "Médecin", "Spécialité", "Région", "Partenaire", "Nombre Visites", "Écart Total", 
      "Type Alerte", "Délégués Impliqués", "Visite N°", "Date et Heure Visite", 
      "Délégué Visiteur", "Cycle Actif", "Semaine Cycle", "Période Semaine", "Statut Planning"
    ];

    const rows = [];
    listToExport.forEach(item => {
      const alertType = item.isSameDelegate 
        ? `Même délégué (${item.delegatesNamesList?.[0] || item.delegate1Name})`
        : `Délégués différents (${item.uniqueDelegatesCount || 2})`;
      const delegatesList = item.delegatesNamesList ? item.delegatesNamesList.join(' & ') : `${item.delegate1Name} & ${item.delegate2Name}`;

      (item.visits || []).forEach(v => {
        let planningStatus = "Hors cycle actif";
        let cycleName = "N/A";
        let weekStr = "N/A";
        let periodStr = "N/A";

        if (v.context) {
          cycleName = v.context.cycleName || "N/A";
          weekStr = `Semaine ${v.context.weekNumber}`;
          periodStr = `${v.context.startShort} au ${v.context.endShort}`;
          if (v.context.isPlannedThisWeek) planningStatus = "Prévu cette semaine";
          else if (v.context.plannedInOtherWeek) planningStatus = `Prévu en Semaine ${v.context.plannedInOtherWeek}`;
          else planningStatus = "Hors planning cycle";
        }

        rows.push([
          `"${item.name || ''}"`, `"${item.specialtyName || ''}"`, `"${item.regionName || ''}"`,
          item.isPartner ? "OUI" : "NON", item.totalVisitsCount || 2, `"${item.totalSpanDays || item.daysDiff}"`,
          `"${alertType}"`, `"${delegatesList}"`, `Visite ${v.visitIndex || 1}`, `"${v.dateFull || ''}"`,
          `"${v.delegateName || ''}"`, `"${cycleName}"`, `"${weekStr}"`, `"${periodStr}"`, `"${planningStatus}"`
        ]);
      });
    });

    const csvContent = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `visites_rapprochees_${selectedCategoryFilter !== 'all' ? selectedCategoryFilter + '_' : ''}${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    message.success("Export téléchargé !");
  };

  // ALERTES 2 : CONFLITS D'ASSIGNATION
  const fetchAssignmentConflicts = useCallback(async () => {
    setLoadingConflicts(true);
    try {
      const params = {};
      if (selectedCategoryFilter && selectedCategoryFilter !== 'all') params.category = selectedCategoryFilter;
      if (selectedDelegateFilter) params.delegateId = selectedDelegateFilter;
      if (selectedDateRangeFilter && selectedDateRangeFilter[0] && selectedDateRangeFilter[1]) {
        params.startDate = selectedDateRangeFilter[0].format('YYYY-MM-DD');
        params.endDate = selectedDateRangeFilter[1].format('YYYY-MM-DD');
      }
      const res = await getAllDoctorConflicts(params);
      setAssignmentConflicts(res.data || []);
    } catch (e) {
      console.error("Erreur chargement conflits assignation:", e);
    } finally {
      setLoadingConflicts(false);
    }
  }, [selectedCategoryFilter, selectedDelegateFilter, selectedDateRangeFilter]);

  useEffect(() => {
    fetchAssignmentConflicts();
  }, [fetchAssignmentConflicts]);

  const handleExportAssignmentConflicts = () => {
    if (!groupedAssignmentConflicts || groupedAssignmentConflicts.length === 0) {
      message.warning("Aucun conflit d'assignation à exporter.");
      return;
    }

    const headers = [
      "ID Médecin", "Médecin", "Spécialité", "Région", "Partenaire",
      "Premier Délégué", "Cycles Délégué 1",
      "Second Délégué", "Cycles Délégué 2"
    ];

    const rows = groupedAssignmentConflicts.map(item => {
      const cycles1Str = item.conflictsList
        .map(c => `${c.cycle1Name} (S${c.week1Number}: ${c.week1Start} au ${c.week1End})`)
        .join(' - ');

      const cycles2Str = item.conflictsList
        .map(c => `${c.cycle2Name} (S${c.week2Number}: ${c.week2Start} au ${c.week2End})`)
        .join(' - ');

      return [
        item.personId,
        `"${item.doctorName || ''}"`,
        `"${item.specialtyName || ''}"`,
        `"${item.regionName || ''}"`,
        item.isPartner ? "OUI" : "NON",
        `"${item.delegate1Name || ''}"`,
        `"${cycles1Str}"`,
        `"${item.delegate2Name || ''}"`,
        `"${cycles2Str}"`
      ];
    });

    const csvContent = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `conflits_assignation_medecins_${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    message.success("Export des conflits téléchargé avec succès !");
  };

  const conflictTableColumns = [
    {
      title: 'Médecin',
      key: 'doctor',
      width: 250,
      render: (_, r) => {
        const cat = r.relationshipCategory || (r.isPartner ? 'PARTNER_ACTIVE' : 'STANDARD');
        const catConfig = RELATIONSHIP_CONFIG[cat] || RELATIONSHIP_CONFIG.STANDARD;

        return (
          <Space direction="vertical" size={2}>
            <Space>
              <Text 
                strong 
                style={{ color: catConfig.titleColor, cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }} 
                onClick={() => handleOpenDoctorHistory(r.personId)}
              >
                {r.doctorName}
              </Text>
            </Space>
            <Space size={4} wrap>
              <Tag color={catConfig.tagColor} style={{ fontWeight: 'bold', fontSize: '11px', padding: '1px 6px', margin: 0 }}>
                {catConfig.icon} {catConfig.label}
              </Tag>
              <Tag color="blue">{r.specialtyName}</Tag>
              <Tag color="cyan">{r.regionName}</Tag>
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Premier Délégué & Planning',
      key: 'plan1',
      render: (_, r) => {
        const cat1 = allKnownDelegates.find(d => Number(d.id) === Number(r.delegate1Id))?.category;
        return (
          <div style={{ background: '#fff7e6', padding: '8px 12px', borderRadius: 6, border: '1px solid #ffd591' }}>
            <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Text strong style={{ color: '#d46b08', fontSize: '13px' }}>👤 {r.delegate1Name}</Text>
              {cat1 && (
                <Tag color={getCategoryTagColor(cat1)} style={{ fontSize: '10px', fontWeight: 'bold', margin: 0 }}>
                  {cat1}
                </Tag>
              )}
              <Tag color="orange" style={{ margin: 0, fontSize: '10px', fontWeight: 'bold' }}>
                {r.conflictsList.length} cycle(s) en conflit
              </Tag>
            </div>
            <Space wrap size={[6, 6]}>
              {r.conflictsList.map((c, idx) => (
                <Tooltip key={idx} title={`Période : ${c.week1Start} au ${c.week1End}`}>
                  <Tag color="orange" style={{ margin: 0, padding: '3px 8px', fontSize: '11px', cursor: 'help' }}>
                    <b>{c.cycle1Name}</b> (S{c.week1Number})
                    <span style={{ fontSize: '10px', opacity: 0.85, marginLeft: 4 }}>
                      [{dayjs(c.week1Start).format('DD/MM')} - {dayjs(c.week1End).format('DD/MM')}]
                    </span>
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </div>
        );
      },
    },
    {
      title: 'Second Délégué en Conflit',
      key: 'plan2',
      render: (_, r) => {
        const cat2 = allKnownDelegates.find(d => Number(d.id) === Number(r.delegate2Id))?.category;
        return (
          <div style={{ background: '#fff1f0', padding: '8px 12px', borderRadius: 6, border: '1px solid #ffa39e' }}>
            <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Text strong style={{ color: '#cf1322', fontSize: '13px' }}>👤 {r.delegate2Name}</Text>
              {cat2 && (
                <Tag color={getCategoryTagColor(cat2)} style={{ fontSize: '10px', fontWeight: 'bold', margin: 0 }}>
                  {cat2}
                </Tag>
              )}
              <Tag color="red" style={{ margin: 0, fontSize: '10px', fontWeight: 'bold' }}>
                {r.conflictsList.length} cycle(s) en conflit
              </Tag>
            </div>
            <Space wrap size={[6, 6]}>
              {r.conflictsList.map((c, idx) => (
                <Tooltip key={idx} title={`Période : ${c.week2Start} au ${c.week2End}`}>
                  <Tag color="red" style={{ margin: 0, padding: '3px 8px', fontSize: '11px', cursor: 'help' }}>
                    <b>{c.cycle2Name}</b> (S{c.week2Number})
                    <span style={{ fontSize: '10px', opacity: 0.85, marginLeft: 4 }}>
                      [{dayjs(c.week2Start).format('DD/MM')} - {dayjs(c.week2End).format('DD/MM')}]
                    </span>
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </div>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, r) => (
        <Button
          size="small"
          type="primary"
          ghost
          icon={<AuditOutlined />}
          onClick={() => handleOpenDoctorHistory(r.personId)}
        >
          Parcours
        </Button>
      ),
    },
  ];

  const filteredCycles = useMemo(() => {
    let tempCycles = [...(allCycles || [])];

    if (selectedCategoryFilter && selectedCategoryFilter !== 'all') {
      tempCycles = tempCycles.filter(cycle => {
        const delCategory = cycle.delegate?.category || 
          allKnownDelegates.find(d => Number(d.id) === Number(cycle.delegate?.id))?.category;
        return delCategory === selectedCategoryFilter;
      });
    }

    if (selectedDelegateFilter != null) {
      tempCycles = tempCycles.filter(cycle => String(cycle.delegate?.id) === String(selectedDelegateFilter));
    }

    const [filterStartDate, filterEndDate] = selectedDateRangeFilter;
    if (filterStartDate || filterEndDate) { 
      tempCycles = tempCycles.filter(cycle => { 
        const { overallStartDate: s, overallEndDate: e } = getCyclePeriod(cycle.weeks); 
        if (!s || !e) return false; 
        const dS = dayjs(s); 
        const dE = dayjs(e); 
        const sAFE = filterEndDate ? dS.isAfter(filterEndDate, 'day') : false; 
        const eBFS = filterStartDate ? dE.isBefore(filterStartDate, 'day') : false; 
        return !(sAFE || eBFS); 
      }); 
    }
    return tempCycles;
  }, [allCycles, selectedCategoryFilter, selectedDelegateFilter, selectedDateRangeFilter, allKnownDelegates]);

  const expandedRowRender = (record) => {
    const sortedWeeks = [...(record.weeks || [])].sort((a, b) => (a.week_number ?? Infinity) - (b.week_number ?? Infinity));
    
    const weekItems = sortedWeeks.map((week) => {
        const personsInWeek = week.persons || [];
        return (
            <Descriptions.Item 
                key={week.id || `week-${week.week_number}`} 
                label={<Text strong>Semaine {week.week_number}</Text>} 
                labelStyle={{ width: '150px', verticalAlign: 'top' }}
            >
                <div>
                    <Text>{formatDate(week.start_date)} au {formatDate(week.end_date)}</Text>
                    
                    {personsInWeek.length > 0 && (
                        <div style={{ marginTop: '8px', borderLeft: '2px solid #1890ff', paddingLeft: '16px' }}>
                            <Text strong>Médecins à visiter ({personsInWeek.length}):</Text>
                            <List
                                size="small"
                                dataSource={personsInWeek}
                                renderItem={person => (
                                    <List.Item style={{ padding: '2px 0', border: 'none' }}>
                                        <UserOutlined style={{ marginRight: 8, color: '#595959' }} />
                                        {`${person.firstname || ''} ${person.lastname || ''}`.trim()}
                                    </List.Item>
                                )}
                                style={{ maxHeight: '150px', overflowY: 'auto' }}
                            />
                        </div>
                    )}
                </div>
            </Descriptions.Item>
        );
    });

    const allAssignedPersons = record.persons || [];
    const personCount = allAssignedPersons.length;
    
    return (
        <Descriptions bordered size="small" column={1} style={{ background: '#f9f9f9', padding: '12px 24px', margin: '0 48px' }}>
            {weekItems.length > 0 ? weekItems : <Descriptions.Item label={<Text strong>Périodes</Text>}><Text type="secondary">Aucune période définie.</Text></Descriptions.Item>}
            
            <Descriptions.Item 
                key="all_persons_summary" 
                label={<Text strong>Total Personnes Uniques</Text>}
                labelStyle={{ width: '150px' }}
            >
                {personCount > 0 ? `${personCount} personne(s) assignée(s) au total dans ce cycle.` : 'Aucune personne assignée.'}
            </Descriptions.Item>
        </Descriptions>
    );
  };

  const columns = [
    { title: 'Nom du Cycle', dataIndex: 'name', key: 'name', render: (text) => text || <Text type="secondary">Sans Nom</Text>, ellipsis: true },
    { 
      title: 'Délégué', 
      key: 'delegate', 
      render: (_, r) => {
        if (!r.delegate) return 'N/A';
        const cat = r.delegate.category || allKnownDelegates.find(d => d.id === r.delegate.id)?.category;
        return (
          <Space>
            <span>{r.delegate.firstname} {r.delegate.lastname}</span>
            {cat && (
              <Tag color={getCategoryTagColor(cat)} style={{ fontSize: '10px', fontWeight: 'bold', margin: 0 }}>
                {cat}
              </Tag>
            )}
          </Space>
        );
      }, 
      ellipsis: true 
    },
    { title: 'Période Cycle', key: 'cyclePeriod', width: 180, align: 'center', render: (_, r) => { const { overallStartDate: s, overallEndDate: e } = getCyclePeriod(r.weeks); return (s && e) ? `${formatDate(s)} - ${formatDate(e)}` : <Text type="secondary">N/D</Text>; }, sorter: (a, b) => { const sA = getCyclePeriod(a.weeks).overallStartDate; const sB = getCyclePeriod(b.weeks).overallStartDate; if (!sA) return 1; if (!sB) return -1; return dayjs(sA).unix() - dayjs(sB).unix(); }, defaultSortOrder: 'descend' },
    { title: 'Nb Sem.', dataIndex: ['weeks', 'length'], key: 'weeksCount', align: 'center', width: 80 },
    { title: 'Obj/Sem', dataIndex: 'target_visits_per_week', key: 'target_visits_per_week', align: 'center', width: 80 },
    { 
        title: 'Nb Pers.', 
        key: 'personsCount', 
        align: 'center', 
        width: 80,
        render: (_, record) => {
            const allPersonIds = new Set((record.weeks || []).flatMap(week => (week.persons || []).map(p => p.id)));
            return allPersonIds.size;
        }
    },
    {
        title: 'Actions',
        key: 'actions',
        align: 'center',
        width: 180,
        fixed: 'right',
        render: (_, record) => (
            <Space size="small">
                <Button 
                    icon={<EditOutlined />} 
                    onClick={() => showEditModal(record)} 
                    title="Modifier le Cycle et la Planification" 
                    size="small" 
                    type="primary" 
                    ghost 
                />
                
                <Button 
                    icon={<UploadOutlined />} 
                    onClick={() => handleOpenWeeklyPlanUploadModal(record)} 
                    title="Uploader un Plan Hebdomadaire (CSV)" 
                    size="small" 
                />
                
                <Button 
                    icon={<DownloadOutlined />} 
                    onClick={() => handleDownloadWeeklyPlan(record.id, record.name)} 
                    title="Télécharger le Plan Hebdomadaire (CSV)" 
                    size="small" 
                    disabled={!record.weeks || !record.weeks.some(week => week.persons && week.persons.length > 0)}
                />
                
                <Popconfirm title="Supprimer ce cycle ?" onConfirm={() => handleDelete(record.id)} okText="Oui" cancelText="Non" okButtonProps={{ danger: true }}>
                    <Button icon={<DeleteOutlined />} danger title="Supprimer le Cycle" size="small" />
                </Popconfirm>
            </Space>
        )
    }
  ];

  return (
    <>
      <Title level={3} style={{ marginBottom: '16px' }}>Gestion des Cycles de Visite</Title>

      {/* --- ZONE DE FILTRES ET BOUTONS D'IMPORTATION --- */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px', padding: '16px 16px 0 16px', background: '#f0f2f5', borderRadius: '8px' }}>
         <Col xs={24} sm={24} md={18} lg={17}>
             <Row gutter={[16, 8]}>
                 <Col xs={24} sm={8} lg={7}>
                     <Text strong style={{ display: 'block', marginBottom: '8px' }}><AppstoreOutlined /> Gamme :</Text>
                     <Select 
                        style={{ width: '100%' }}
                        value={selectedCategoryFilter}
                        onChange={(val) => {
                            setSelectedCategoryFilter(val);
                            if (val !== 'all' && selectedDelegateFilter) {
                                const cur = allKnownDelegates.find(d => Number(d.id) === Number(selectedDelegateFilter));
                                if (cur && cur.category !== val) {
                                    setSelectedDelegateFilter(undefined);
                                }
                            }
                        }}
                     >
                        <Select.Option value="all">Toutes les gammes</Select.Option>
                        <Select.Option value="AKTIV"><Tag color="blue" style={{ fontWeight: 'bold', margin: 0 }}>AKTIV</Tag></Select.Option>
                        <Select.Option value="SANOSAN"><Tag color="green" style={{ fontWeight: 'bold', margin: 0 }}>SANOSAN</Tag></Select.Option>
                        <Select.Option value="MATERNITE"><Tag color="purple" style={{ fontWeight: 'bold', margin: 0 }}>MATERNITE</Tag></Select.Option>
                     </Select>
                 </Col>

                 <Col xs={24} sm={8} lg={8}>
                     <Text strong style={{ display: 'block', marginBottom: '8px' }}><FilterOutlined /> Délégué :</Text>
                     <Select 
                        style={{ width: '100%' }} 
                        placeholder="Tous les délégués" 
                        loading={loadingDelegates} 
                        onChange={handleDelegateFilterChange} 
                        value={selectedDelegateFilter} 
                        allowClear 
                        showSearch 
                        optionFilterProp="label" 
                        filterOption={(i, o) => (o?.label ?? '').toLowerCase().includes(i.toLowerCase())} 
                        notFoundContent={loadingDelegates ? <Spin size="small" /> : "Aucun"} 
                        options={[
                            { value: undefined, label: 'Tous les délégués' }, 
                            ...availableDelegatesForSelect.map(d => ({ 
                                value: d.id, 
                                label: `${d.firstname || ''} ${d.lastname || ''} ${d.category ? `(${d.category})` : ''}`.trim() || `ID:${d.id}` 
                            }))
                        ]} 
                     />
                     {errorDelegates && <Alert message={errorDelegates} type="warning" showIcon style={{ marginTop: 8 }} />}
                 </Col>

                 <Col xs={24} sm={8} lg={9}>
                     <Text strong style={{ display: 'block', marginBottom: '8px' }}><CalendarOutlined /> Période :</Text>
                     <RangePicker style={{ width: '100%' }} value={selectedDateRangeFilter} onChange={handleDateRangeFilterChange} format={dateFormat} allowClear placeholder={['Début Cycle', 'Fin Cycle']} />
                 </Col>
             </Row>
         </Col>

         <Col xs={24} sm={24} md={6} lg={7} style={{ textAlign: 'right', alignSelf: 'flex-start' }}>
            <Space direction="vertical" align="end" style={{ width: '100%' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal} style={{ marginBottom: 8 }}> Ajouter un Cycle </Button>
                <Text strong style={{ fontSize: '0.9em' }}>Importer Cycles via CSV :</Text>
                <Space.Compact style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Upload {...uploadCycleProps} style={{ flexShrink: 1 }}>
                        <Button icon={<UploadOutlined />} loading={uploadingCycles && !parsedCycleData}> {cycleFileList.length > 0 ? cycleFileList[0].name : 'Choisir Fichier Cycles'} </Button>
                    </Upload>
                    <Button type={parsedCycleData && !parseCycleError ? "primary" : "default"} onClick={handleImportParsedCycleData} disabled={!parsedCycleData || parsedCycleData.length === 0 || !!parseCycleError || uploadingCycles} loading={uploadingCycles && !!parsedCycleData} > {uploadingCycles ? 'Import...' : 'Importer Cycles'} </Button>
                 </Space.Compact>
                 <Text type="secondary" style={{ fontSize: '0.8em' }}><Link href="/templates/cycle_import_template.csv" target="_blank" download="cycle_import_template.csv">Télécharger modèle (Cycles)</Link></Text>
                 {parseCycleError && (<Alert message={parseCycleError} type="error" showIcon style={{ width: '100%', textAlign: 'left', marginTop: 5 }} />)}
                 {parsedCycleData && parsedCycleData.length > 0 && !parseCycleError && (<Alert message={`${parsedCycleData.length} cycles prêts.`} type="success" showIcon style={{ width: '100%', textAlign: 'left', marginTop: 5 }} />)}
             </Space>
         </Col>
      </Row>

      {planningError && !loadingCycles && (
        <Alert message="Erreur chargement cycles" description={planningError?.message || JSON.stringify(planningError)} type="error" showIcon closable style={{ marginBottom: 16 }} />
      )}

      {/* --- TABLEAU DES CYCLES --- */}
      <Table
        columns={columns}
        dataSource={filteredCycles}
        loading={loadingCycles}
        rowKey="id"
        bordered
        expandable={{ expandedRowRender, rowExpandable: record => record.weeks?.length > 0 || record.persons?.length > 0 }}
        size="small"
        locale={{ emptyText: loadingCycles ? 'Chargement...' : 'Aucun cycle trouvé pour ces filtres.' }}
        scroll={{ x: 'max-content' }}
        style={{ marginBottom: 24 }}
      />

      {/* ========================================================================= */}
      {/* --- BLOC 1 : CONFLITS D'ASSIGNATION DE MÉDECINS --- */}
      {/* ========================================================================= */}
      <div style={{ marginBottom: 20 }}>
      <Collapse
        defaultActiveKey={['doctor_assignment_conflicts']}
        style={{ 
          borderRadius: 8, 
          border: '1px solid #ffa39e', 
          background: '#fff1f0' 
        }}
        items={[
          {
            key: 'doctor_assignment_conflicts',
            label: (
              <Space>
                <WarningOutlined style={{ color: '#cf1322', fontSize: '16px' }} />
                <span style={{ color: '#cf1322', fontWeight: 'bold', fontSize: '14px' }}>
                  Alertes : Conflits d'Assignation Médecins (Même semaine / Multi-Délégués)
                </span>
                <Badge 
                  count={groupedAssignmentConflicts.length} 
                  overflowCount={99999} 
                  style={{ backgroundColor: '#ff4d4f' }} 
                />
              </Space>
            ),
              extra: (
                <Space onClick={e => e.stopPropagation()}>
                  <Button 
                    size="small" 
                    icon={<DownloadOutlined />} 
                    onClick={handleExportAssignmentConflicts}
                    disabled={groupedAssignmentConflicts.length === 0}
                    style={{ borderColor: '#ffa39e', color: '#cf1322' }}
                  >
                    Exporter CSV
                  </Button>
                  <Button 
                    size="small" 
                    icon={<ReloadOutlined />} 
                    onClick={fetchAssignmentConflicts}
                  >
                    Actualiser
                  </Button>
                </Space>
              ),
              children: (
                <Spin spinning={loadingConflicts}>
                  {groupedAssignmentConflicts.length > 0 ? (
                    <Table
                      dataSource={groupedAssignmentConflicts}
                      columns={conflictTableColumns}
                      rowKey="key"
                      rowClassName={(r) => {
                        const cat = r.relationshipCategory || (r.isPartner ? 'PARTNER_ACTIVE' : 'STANDARD');
                        if (cat === 'REFRACTORY') return 'row-refractory-red';
                        if (cat === 'PARTNER_AT_RISK') return 'row-partner-warning';
                        if (cat === 'KEY_PRESCRIBER') return 'row-prescriber-green';
                        if (cat === 'PARTNER_ACTIVE') return 'row-partner-gold';
                        return '';
                      }}
                      pagination={{ pageSize: 5, size: 'small' }}
                      size="small"
                      bordered
                      style={{ background: '#fff' }}
                    />
                  ) : (
                    <Alert 
                      message="Aucun conflit d'assignation simultanée détecté pour les filtres actifs." 
                      type="success" 
                      showIcon 
                    />
                  )}
                </Spin>
              )
            }
          ]}
        />
      </div>

      {/* ========================================================================= */}
      {/* --- BLOC 2 : VISITES RAPPROCHÉES (< 5 JOURS) --- */}
      {/* ========================================================================= */}
      <div style={{ marginBottom: 20 }}>
        <Collapse
          defaultActiveKey={['frequent_visits']}
          style={{ 
            borderRadius: 8, 
            border: '1px solid #ffadd2', 
            background: '#fff0f6' 
          }}
          items={[
            {
              key: 'frequent_visits',
              label: (
                <Space>
                  <ClockCircleOutlined style={{ color: '#c41d7f', fontSize: '16px' }} />
                  <span style={{ color: '#c41d7f', fontWeight: 'bold', fontSize: '14px' }}>
                    Alertes : Visites Rapprochées (&lt; 5 jours)
                  </span>
                  <Badge count={filteredFrequentVisits.length} overflowCount={99999} style={{ backgroundColor: '#eb2f96' }} />
                </Space>
              ),
              extra: (
                <Space onClick={e => e.stopPropagation()} wrap>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#c41d7f' }}>Écart :</span>
                  <Select
                    size="small"
                    value={selectedDaysFilter}
                    onChange={(val) => setSelectedDaysFilter(val)}
                    style={{ width: 145 }}
                    onClick={(e) => e.stopPropagation()}
                    options={[
                      { value: 'all', label: 'Tous (1 à 5 jours)' },
                      { value: '1', label: '⏱️ 1 jour' },
                      { value: '2', label: '⏱️ 2 jours' },
                      { value: '3', label: '⏱️ 3 jours' },
                      { value: '4', label: '⏱️ 4 jours' },
                      { value: '5', label: '⏱️ 5 jours' },
                      { value: 'lte_2', label: '⚡ ≤ 2 jours' },
                      { value: 'lte_3', label: '⚡ ≤ 3 jours' },
                    ]}
                  />

                  <Button 
                    size="small" 
                    icon={<DownloadOutlined />} 
                    onClick={handleExportFrequentVisits}
                    disabled={filteredFrequentVisits.length === 0}
                    style={{ borderColor: '#ffadd2', color: '#c41d7f' }}
                  >
                    Exporter CSV
                  </Button>
                  <Button 
                    size="small" 
                    icon={<ClockCircleOutlined />} 
                    onClick={fetchFrequentVisits}
                  >
                    Actualiser
                  </Button>
                </Space>
              ),
              children: (
                <Spin spinning={loadingFrequentVisits}>
                  {filteredFrequentVisits.length > 0 ? (
                    <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                      <List
                        itemLayout="horizontal"
                        dataSource={filteredFrequentVisits}
                        renderItem={item => (
                          <List.Item
                            style={{ 
                              padding: '12px 16px', 
                              background: '#ffffff', 
                              borderRadius: 6, 
                              marginBottom: 8, 
                              border: '1px solid #ffd6e7' 
                            }}
                            actions={[
                              <Button 
                                size="small" 
                                type="primary" 
                                ghost 
                                icon={<AuditOutlined />} 
                                onClick={() => handleOpenDoctorHistory(item.personId)}
                              >
                                Parcours
                              </Button>
                            ]}
                          >
                            <List.Item.Meta
                              avatar={
                                <Avatar 
                                  icon={<UserOutlined />} 
                                  style={{ backgroundColor: '#ffd6e7', color: '#c41d7f', cursor: 'pointer' }} 
                                  onClick={() => handleOpenDoctorHistory(item.personId)}
                                />
                              }
                              title={
                                <Space wrap align="center">
                                  <Text 
                                    strong 
                                    style={{ cursor: 'pointer', color: '#1890ff', fontSize: '14px' }} 
                                    onClick={() => handleOpenDoctorHistory(item.personId)}
                                  >
                                    {item.name}
                                  </Text>
                                  {item.isPartner && <Tag color="gold"><StarFilled /> PARTENAIRE</Tag>}
                                  <Tag color="blue">{item.specialtyName}</Tag>
                                  <Tag color="cyan">{item.regionName}</Tag>
                                  <Tag color="error" style={{ fontWeight: 'bold' }}>
                                    ⚠️ {item.totalVisitsCount} visites en {item.totalSpanDays}
                                  </Tag>
                                  {item.isSameDelegate ? (
                                    <Tag color="magenta">Même délégué : {item.delegatesNamesList[0]}</Tag>
                                  ) : (
                                    <Tag color="purple">👥 {item.uniqueDelegatesCount} délégués : {item.delegatesNamesList.join(', ')}</Tag>
                                  )}
                                </Space>
                              }
                              description={
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                                  {item.visits?.map((v, vIdx) => (
                                    <div 
                                      key={vIdx} 
                                      style={{ 
                                        background: vIdx % 2 === 0 ? '#fff0f6' : '#f9f0ff', 
                                        padding: '6px 10px', 
                                        borderRadius: 4, 
                                        border: vIdx % 2 === 0 ? '1px solid #ffadd2' : '1px solid #d3adf7',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 6
                                      }}
                                    >
                                      <div>
                                        <Text strong style={{ color: vIdx % 2 === 0 ? '#c41d7f' : '#531dab', fontSize: '11px', marginRight: 8 }}>
                                          Visite {v.visitIndex} :
                                        </Text>
                                        <Tag color="default" style={{ fontSize: '11px' }}>{v.dateFull}</Tag>
                                        <Text strong style={{ fontSize: '11px', marginLeft: 4 }}>👤 {v.delegateName}</Text>
                                      </div>

                                      {v.context ? (
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                                          <Tag color="geekblue" style={{ fontSize: '11px', margin: 0 }}>
                                            {v.context.cycleName} - S{v.context.weekNumber} ({v.context.startShort} au {v.context.endShort})
                                          </Tag>
                                          {v.context.isPlannedThisWeek ? (
                                            <Tag color="green" style={{ fontSize: '11px', margin: 0 }}>✅ Prévu cette semaine</Tag>
                                          ) : v.context.plannedInOtherWeek ? (
                                            <Tag color="orange" style={{ fontSize: '11px', margin: 0 }}>⚠️ Prévu en S{v.context.plannedInOtherWeek}</Tag>
                                          ) : (
                                            <Tag color="default" style={{ fontSize: '11px', margin: 0 }}>❌ Hors planning</Tag>
                                          )}

                                          {v.conflictCompliance && (
                                            v.conflictCompliance.isRespected ? (
                                              <Tag color="success" style={{ fontSize: '11px', margin: 0, fontWeight: 'bold', border: '1px solid #b7eb8f' }}>
                                                ✅ Jour respecté ({v.conflictCompliance.actualDay})
                                              </Tag>
                                            ) : (
                                              <Tag color="error" style={{ fontSize: '11px', margin: 0, fontWeight: 'bold', border: '1px solid #ffa39e' }}>
                                                ❌ Non respecté : Visité {v.conflictCompliance.actualDay} au lieu de {v.conflictCompliance.expectedDay}
                                              </Tag>
                                            )
                                          )}
                                        </div>
                                      ) : (
                                        <Text type="secondary" style={{ fontSize: '11px' }}>Hors cycle actif à cette date</Text>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  ) : (
                    <Alert message="Aucune visite rapprochée (< 5 jours) pour ce délégué." type="info" showIcon />
                  )}
                </Spin>
              )
            }
          ]}
        />
      </div>

      {/* --- MODAL AJOUT / MODIFICATION CYCLE --- */}
      <Modal
          open={isAddEditModalVisible}
          onCancel={handleAddEditCancel}
          footer={null}
          destroyOnClose
          width={900}
          maskClosable={false}
          title={editingCycle ? 'Modifier le Cycle' : 'Ajouter un Cycle'}
      >
        <CycleFormModal
            visible={isAddEditModalVisible}
            onCancel={handleAddEditCancel}
            cycle={editingCycle}
        />
      </Modal>

      {/* --- MODAL ASSIGNATION DE PERSONNES CSV --- */}
      <Modal
          title={`Gérer Personnes pour Cycle: ${currentCycleForPersonManagement?.name || 'ID ' + currentCycleForPersonManagement?.id}`}
          open={isPersonUploadModalVisible}
          onCancel={handlePersonUploadModalCancel}
          footer={[
              <Button key="back" onClick={handlePersonUploadModalCancel}>
                  Fermer
              </Button>,
              <Button
                  key="submit"
                  type="primary"
                  loading={uploadingPersons}
                  onClick={triggerManualPersonUpload}
                  disabled={!fileToUploadForPersons || uploadingPersons}
              >
                  Uploader et Assigner Personnes
              </Button>,
          ]}
          destroyOnClose
          width={600}
      >
          <Upload.Dragger {...personUploadProps}>
              <p className="ant-upload-drag-icon">
                  <UsergroupAddOutlined />
              </p>
              <p className="ant-upload-text">Cliquez ou glissez un fichier CSV ici</p>
              <p className="ant-upload-hint">
                  Le fichier CSV doit contenir une colonne "ID" (ou "PersonID", "DoctorID", etc.)
                  avec les identifiants numériques des personnes à assigner.
                  <br/>
                  <Link href="/templates/persons_for_cycle_template.csv" target="_blank" download="persons_for_cycle_template.csv">
                      Télécharger modèle (Personnes)
                  </Link>
              </p>
          </Upload.Dragger>
          {uploadingPersons && <Spin tip="Assignation des personnes en cours..." style={{ marginTop: 16, display: 'block', textAlign: 'center' }} />}
          {personUploadResult && (
              <div style={{ marginTop: 16 }}>
                  {personUploadResult.message && (
                      <Alert
                          message={personUploadResult.message}
                          type={personUploadResult.errors && personUploadResult.errors.length > 0 ? "warning" : "success"}
                          showIcon
                      />
                  )}
                  {personUploadResult.errors && personUploadResult.errors.length > 0 && (
                      <List
                          size="small"
                          header={<div style={{fontWeight: 'bold'}}>Avertissements/Erreurs :</div>}
                          bordered
                          dataSource={personUploadResult.errors}
                          renderItem={(item) => <List.Item><Text type="danger">{item}</Text></List.Item>}
                          style={{ maxHeight: 150, overflowY: 'auto', marginTop: 8 }}
                      />
                  )}
              </div>
          )}
      </Modal>

      {/* --- MODAL UPLOAD PLAN HEBDOMADAIRE CSV --- */}
      <Modal
          title={`Uploader le Plan Hebdomadaire pour: ${currentCycleForPersonManagement?.name}`}
          open={isWeeklyPlanUploadModalVisible}
          onCancel={handleWeeklyPlanUploadModalCancel}
          footer={[
              <Button key="back" onClick={handleWeeklyPlanUploadModalCancel}>Annuler</Button>,
              <Button
                  key="submit"
                  type="primary"
                  loading={uploadingWeeklyPlan}
                  onClick={triggerManualWeeklyPlanUpload}
                  disabled={!weeklyPlanFile}
              >
                  Mettre à jour le Plan
              </Button>,
          ]}
          destroyOnClose
      >
          <Upload.Dragger
              name="file"
              multiple={false}
              beforeUpload={handleBeforeWeeklyPlanUpload}
              onRemove={() => setWeeklyPlanFile(null)}
              accept=".csv"
          >
              <p className="ant-upload-drag-icon"><UsergroupAddOutlined /></p>
              <p className="ant-upload-text">Cliquez ou glissez un fichier CSV ici</p>
              <p className="ant-upload-hint">Le fichier doit contenir les colonnes 'ID' et 'NumeroSemaine'.</p>
          </Upload.Dragger>
      </Modal>

      {/* ========================================================================= */}
      {/* ⚡ MODALE PARCOURS DE VISITE DU MÉDECIN ⚡ */}
      {/* ========================================================================= */}
      <DoctorHistoryModal
        visible={doctorModalVisible}
        onClose={() => setDoctorModalVisible(false)}
        personId={selectedDoctorId}
        zIndex={2000}
      />

      {/* STYLES DES LIGNES EN FONCTION DU STATUT DU MÉDECIN */}
      <style>{`
        .ant-table-tbody > tr.row-refractory-red > td { background-color: #fff1f0 !important; }
        .ant-table-tbody > tr.row-refractory-red:hover > td { background-color: #ffccc7 !important; }

        .ant-table-tbody > tr.row-partner-gold > td { background-color: #fffbe6 !important; }
        .ant-table-tbody > tr.row-partner-gold:hover > td { background-color: #fff1b8 !important; }

        .ant-table-tbody > tr.row-partner-warning > td { background-color: #fff7e6 !important; }
        .ant-table-tbody > tr.row-partner-warning:hover > td { background-color: #ffe7ba !important; }

        .ant-table-tbody > tr.row-prescriber-green > td { background-color: #f6ffed !important; }
        .ant-table-tbody > tr.row-prescriber-green:hover > td { background-color: #d9f7be !important; }
      `}</style>
    </>
  );
}

export default CyclesPage;